/* eslint-disable id-length, max-statements, max-lines-per-function */
import { merge, isEmpty, isEqual, sortBy } from "lodash";
import React, { useState, SetStateAction } from "react";
import * as yup from "yup";
import { Formik, Form } from "formik";
import { Grid } from "@material-ui/core";
import { emptyDelta } from "components/message-editor";
import { quillDeltaToHtml, quillDeltaToPlaintext } from "components/message-editor/utils";
import { InputWithErrorLabel } from "components/haven-design/form/inputs";
import { CheckboxWithErrorLabel } from "components/haven-design/form/checkbox";
import { MultiSelect } from "components/haven-design/form";
import { SubmitButton } from "components/buttons";
import { TemplateType, TemplateGroupCreateInput } from "types/gql-types";
import { Viewer } from "components/auth/types";
import { Button } from "components/haven-design/buttons";
import { CopyConfirmationButton } from "components/send-messages/copy-confirmation";
import { useSnackbar } from "components/haven-design/snackbar";
import { Delta } from "components/message-editor/types";
import { ChannelForm } from "components/send-messages/channel-form";
import { PromptOnNavigate } from "utils/use-prompt";
import styles from "./template-group.module.css";

const SMS_CHARACTER_LIMIT = 280;
const NAME_LIMIT = 200;
const NO_CHANNEL_CONTENT_ERROR =
  "Please add a message for at least one channel (SMS, Email, or Letter).";
const NO_EMAIL_SUBJECT_ERROR = "Please add a Subject to your Email template.";


interface PropertyOption {
  label: string;
  id: string;
}
interface Props {
  viewer: Viewer;
  isSending: boolean;
  handleSubmit: (templateData: TemplateGroupCreateInput) => void;
  initialValues?: InitialValues | null;
  smsValue?: Delta | null;
  smsId?: string | null;
  letterId?: string | null;
  emailId?: string | null;
  letterValue?: Delta | null;
  emailValue?: Delta | null;
  emailSubject?: string | null;
  onCancel?(): void;
  readOnly?: boolean;
}

type InitialValues = {
  template: { name: string };
  orgTemplate: boolean;
  propertyTemplate: boolean;
  propertyIds: PropertyOption[];
};

const html = (editorInfo: Delta, type: string) => {
  if (type === TemplateType.SMS) return quillDeltaToPlaintext(editorInfo);
  return quillDeltaToHtml(editorInfo);
};

const renderPropertyCheckBox = (
  props: Props,
  propertyTemplate: boolean,
  setFieldValue: (name: string, value: boolean, shouldValidate?: boolean) => void,
) => {
  const { initialValues, viewer } = props;
  if (viewer && viewer.lists) {
    const gqlToOption = (x: { id: string; name: string }) => ({ id: x.id, label: x.name });
    const propOpts = sortBy(viewer.lists.map(gqlToOption), "label");
    return (
      <>
        <CheckboxWithErrorLabel
          checked={propertyTemplate}
          label={`Share with users at your  ${
            viewer && viewer.lists && viewer.lists.length > 1 ? "properties" : "property"
          }`}
          name="propertyTemplate"
          onChange={(_e, isChecked) => {
            setFieldValue("propertyTemplate", isChecked);
            if (isChecked) setFieldValue("orgTemplate", false);
          }}
        />
        {propertyTemplate && (
          <div className={styles.select}>
            <MultiSelect
              name="propertyIds"
              label="Select properties to share this template"
              options={propOpts}
              selectedOptions={
                initialValues && initialValues.propertyIds ? initialValues.propertyIds : []
              }
            />
          </div>
        )}
      </>
    );
  }
  return null;
};

const formSchema = yup.object().shape({
  orgTemplate: yup
    .boolean()
    .toggleSibling("propertyTemplate", "Cannot choose both organization and property."),
  propertyTemplate: yup
    .boolean()
    .toggleSibling("orgTemplate", "Cannot choose both organization and property."),
  propertyIds: yup
    .array()
    .of(yup.object())
    .when("propertyTemplate", (propertyTemplate: boolean, schema: yup.ArraySchema<yup.AnyObjectSchema>) => (
      propertyTemplate ? schema.min(1, "Select at least one property") : schema.min(0)
    )),
  template: yup.object().shape({
    name: yup.string().trim().max(NAME_LIMIT).required(),
  }),
});

const defaultInitialValues = {
  template: { name: "" },
  orgTemplate: false,
  propertyTemplate: false,
  propertyIds: [],
};

export const TemplateForm: React.FC<Props> = (props) => {
  const [smsValue, setSmsValue] = useState(props.smsValue || emptyDelta);
  const [letterValue, setLetterValue] = useState(props.letterValue || emptyDelta);
  const [emailValue, setEmailValue] = useState(props.emailValue || emptyDelta);
  const [emailSubject, setEmailSubject] = useState(props.emailSubject || "");
  const [templatesAreDirty, setTemplatesAreDirty] = useState(false);
  const { viewer } = props;
  const { openSnackbar } = useSnackbar();

  type fnType = React.Dispatch<React.SetStateAction<Delta>>;

  const withTracking = (setFn:fnType):fnType => (value:SetStateAction<Delta>) => {
    setFn(value);
    setTemplatesAreDirty(true);
  };

  const mergeChannelMessages = (values: InitialValues) => merge({}, values, {
    propertyIds: values.propertyIds.map((o: { label: string, id: string }) => o.id),
    templates: [
      {
        id: props.smsId,
        type: TemplateType.SMS,
        body: JSON.stringify(smsValue),
        html: html(smsValue, TemplateType.SMS),
      },
      {
        id: props.emailId,
        type: TemplateType.EMAIL,
        body: JSON.stringify(emailValue),
        html: html(emailValue, TemplateType.EMAIL),
        subject: emailSubject,
      },
      {
        id: props.letterId,
        type: TemplateType.LETTER,
        body: JSON.stringify(letterValue),
        html: html(letterValue, TemplateType.LETTER),
      },
    ],
  });

  const handleSubmit = (values: InitialValues) => {
    const deltaOps = emptyDelta;
    if (
      isEqual(smsValue, deltaOps) &&
      isEqual(emailValue, deltaOps) &&
      isEqual(letterValue, deltaOps)
    ) {
      openSnackbar({ message: NO_CHANNEL_CONTENT_ERROR, status: "error", duration: 5000 });
      return;
    }

    if (!isEqual(emailValue, deltaOps) && emailSubject.length < 1) {
      openSnackbar({ message: NO_EMAIL_SUBJECT_ERROR, status: "error", duration: 5000 });
      return;
    }

    props.handleSubmit(mergeChannelMessages(values));
  };

  return (
    <Formik
      onSubmit={handleSubmit}
      validationSchema={formSchema}
      initialValues={props.initialValues || defaultInitialValues}
    >
      {({ errors, values, setFieldValue, dirty, submitCount }) => (
        <Form>
          <PromptOnNavigate shouldPrompt={submitCount <= 0 && (dirty || templatesAreDirty)} />
          <Grid container>
            <Grid item xs={12} sm={6} md={4}>
              <InputWithErrorLabel
                label="Template Name"
                name="template.name"
                type="name"
                disabled={props.readOnly}
              />
            </Grid>
            {viewer.isSupervisor && (
              <Grid item xs={12}>
                <CheckboxWithErrorLabel
                  checked={values.orgTemplate}
                  label={`Share template with all of ${
                    viewer.organization && viewer.organization.name
                  }?`}
                  name="orgTemplate"
                  onChange={(_e, isChecked) => {
                    setFieldValue("orgTemplate", isChecked);
                    if (isChecked) setFieldValue("propertyTemplate", false);
                  }}
                />
              </Grid>
            )}
            {props.readOnly ? null : (
              <Grid item xs={12}>
                {renderPropertyCheckBox(props, values.propertyTemplate, setFieldValue)}
              </Grid>
            )}
            <Grid item xs={12}>
              <h4 className={styles.channelTitle}>Email Template</h4>
              <div className={styles.channelCopyButton}>
                <CopyConfirmationButton context="Letter" onCopy={() => setLetterValue(emailValue)} valueToReplace={letterValue} />
              </div>
            </Grid>
            <ChannelForm
              placeholder="Email Body"
              subject={emailSubject}
              setSubject={(e) => {
                setEmailSubject(e.target.value);
                setTemplatesAreDirty(true);
              }}
              typePrefix={TemplateType.EMAIL}
              setMessage={withTracking(setEmailValue)}
              messageValue={emailValue}
              readOnly={props.readOnly}
            />
            <Grid item xs={12}>
              <h4 className={styles.channelTitle}>Text Message Template</h4>
            </Grid>
            <ChannelForm
              placeholder="Message Body"
              typePrefix={TemplateType.SMS}
              setMessage={withTracking(setSmsValue)}
              messageValue={smsValue}
              characterLimit={SMS_CHARACTER_LIMIT}
              readOnly={props.readOnly}
            />
            <Grid item xs={12}><hr/></Grid>
            <Grid item xs={12}>
              <h4 className={styles.channelTitle}> Letter Template </h4>
              <div className={styles.channelCopyButton}>
                <CopyConfirmationButton context="Email" onCopy={() => setEmailValue(letterValue)} valueToReplace={emailValue} />
              </div>
            </Grid>
            <ChannelForm
              placeholder="Letter Body"
              typePrefix={TemplateType.LETTER}
              setMessage={withTracking(setLetterValue)}
              messageValue={letterValue}
              readOnly={props.readOnly}
            />
          </Grid>
          <div className={styles.actionContainer}>
            {props.onCancel && (
              <Button
                size="L"
                color="secondary"
                onClick={props.onCancel}
                style={{ marginRight: "15px" }}
              >
                Back
              </Button>
            )}

            <SubmitButton
              isWide
              text={props.readOnly ? "Copy" : "Save"}
              isSaving={props.isSending}
              disabled={props.isSending}
              isInvalid={!isEmpty(errors)}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};
